// frontend/src/components/public/BlockRenderer.tsx
import React from 'react';
import { Box, Typography } from '@mui/material';
import { BlockData } from '../../types/Portfolio';
import HeroBlock from './blocks/HeroBlock';
import MissionVisionBlock from './blocks/MissionVisionBlock';
import ImageHighlightBlock from './blocks/ImageHighlightBlock';
import MetricsBlock from './blocks/MetricsBlock';
import OfferingsBlock from './blocks/OfferingsBlock';
import GalleryBlock from './blocks/GalleryBlock';
import TestimonialsBlock from './blocks/TestimonialsBlock';
import RichTextBlock from './blocks/RichTextBlock';
import ContactFormBlock from './blocks/ContactFormBlock';
import CaseStudiesBlock from './blocks/CaseStudiesBlock';
import TeamBlock from './blocks/TeamBlock';
import UpcomingPerformancesBlock from './blocks/UpcomingPerformancesBlock';
import PastImpactBlock from './blocks/PastImpactBlock';
import AudienceDemographicsBlock from './blocks/AudienceDemographicsBlock';
import MediaReachBlock from './blocks/MediaReachBlock';
import BrandVisibilityBlock from './blocks/BrandVisibilityBlock';
import VideoShowcaseBlock from './blocks/VideoShowcaseBlock';
import LinksBlock from './blocks/LinksBlock';
import ProgrammeInitiativesBlock from './blocks/ProgrammeInitiativesBlock';
import ArtistBlock from './blocks/ArtistBlock';


interface BlockRendererProps {
  sections: BlockData[];
  portfolioTitle: string;
  hasContactForm?: boolean;
  theme?: {
    primaryColor: string;
    accentColor: string;
    fontFamily?: string;
    logo?: string;
  };
}

// Map block types to their components
const BLOCK_COMPONENTS: Record<string, React.ComponentType<any>> = {
  hero: HeroBlock,
  mission_vision: MissionVisionBlock,
  image_highlight: ImageHighlightBlock,
  offerings: OfferingsBlock,
  programme_initiatives: ProgrammeInitiativesBlock,
  gallery: GalleryBlock,
  metrics: MetricsBlock,
  testimonials: TestimonialsBlock,
  rich_text: RichTextBlock,
  contact_form: ContactFormBlock,
  case_studies: CaseStudiesBlock,
  team: TeamBlock,
  upcoming_performances: UpcomingPerformancesBlock,
  past_impact: PastImpactBlock,
  audience_demographics: AudienceDemographicsBlock,
  media_reach: MediaReachBlock,
  brand_visibility: BrandVisibilityBlock,
  video_showcase: VideoShowcaseBlock,
  links: LinksBlock,
  artist: ArtistBlock
};

const BlockRenderer: React.FC<BlockRendererProps> = ({ sections, portfolioTitle, hasContactForm, theme }) => {
  return (
    <Box>
      {sections.map((block) => {
        if (!block.enabled) {
          return null;
        }

        // Get the component for this block type
        const BlockComponent = BLOCK_COMPONENTS[block.type];

        // If block type not found, show error in development
        if (!BlockComponent) {
          if (process.env.NODE_ENV === 'development') {
            return (
              <Box
                key={block.id}
                sx={{
                  py: 4,
                  px: 3,
                  bgcolor: '#fff3cd',
                  borderLeft: '4px solid #ffc107',
                  my: 2
                }}
              >
                <Typography variant="h6" color="error">
                  Block type "{block.type}" not implemented yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Block ID: {block.id}
                </Typography>
              </Box>
            );
          }
          return null;
        }

        // Special handling for contact form - pass portfolioTitle
        const extraProps = block.type === 'contact_form' 
          ? { portfolioTitle } 
          : {};

        return (
          <BlockComponent
            key={block.id}
            block={block.data}
            data={block.data}
            hasContactForm={hasContactForm}
            theme={theme || {}}    
            {...extraProps}
          />
        );
      })}
    </Box>
  );
};

export default BlockRenderer;